import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, Flame, Target, Clock, DollarSign, Users, CheckSquare, Sparkles, BarChart3, ArrowUpRight, ArrowDownRight, ExternalLink, X } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v || 0);
}

function ScoreGauge({ score, size = 80 }) {
  const radius = (size - 8) / 2;
  const circumference = Math.PI * radius;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const offset = circumference * (1 - pct);
  const color = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size / 2 + 12 }}>
      <svg width={size} height={size / 2 + 4} viewBox={`0 0 ${size} ${size / 2 + 4}`}>
        <path d={`M 4 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 4} ${size / 2}`} fill="none" stroke="#e2e8f0" strokeWidth="6" strokeLinecap="round" />
        <path d={`M 4 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 4} ${size / 2}`} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute bottom-0 text-center">
        <span className="text-xl font-bold" style={{ color }}>{score}</span>
        <span className="text-xs text-slate-400">/100</span>
      </div>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-2 w-20">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function TrendBadge({ current, previous, suffix = "" }) {
  if (!previous || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return null;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{pct}%{suffix}
    </span>
  );
}

function SectionTitle({ icon: Icon, title, color }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <h3 className="text-sm font-bold text-slate-700">{title}</h3>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

export default function InsightsModal({ open, onClose, pipelines = [], initialPipelineId = "", onOpenDeal }) {
  const appearance = useAppearance();
  const tc = appearance.colors?.["500"] || "#3B82F6";

  const [pipelineId, setPipelineId] = useState(initialPipelineId || "");
  const [responsavelId, setResponsavelId] = useState("all");
  const [responsaveis, setResponsaveis] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open && initialPipelineId) setPipelineId(initialPipelineId); }, [open, initialPipelineId]);
  useEffect(() => { if (open) axios.get(`${API}/users/responsaveis`).then(r => setResponsaveis(r.data)).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!open || !pipelineId) return;
    setLoading(true);
    const params = new URLSearchParams({ pipeline_id: pipelineId });
    if (responsavelId !== "all") params.set("responsavel_id", responsavelId);
    axios.get(`${API}/insights?${params}`).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, pipelineId, responsavelId]);

  const acaoLabels = { cadastrar_tarefa: "Cadastrar Tarefa", enviar_whatsapp: "Enviar WhatsApp", cadastrar_anotacao: "Anotação", avancar: "Avançar Etapa", retroceder: "Retroceder" };
  const FORECAST_COLORS = ["#64748b", tc, "#10b981"];

  const handleDealClick = (dealId) => { onOpenDeal?.(dealId); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[calc(100%-2rem)] !p-0 !border-0 overflow-hidden bg-white [&>button.absolute]:hidden">
        <div className="flex flex-col h-[85vh]">
          {/* Header with gradient */}
          <div className="flex-shrink-0 px-6 py-5 text-white" style={{ background: `linear-gradient(135deg, ${tc} 0%, ${tc}dd 100%)` }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Insights da Pipeline</h2>
                  <p className="text-xs text-white/70">Inteligência baseada nos dados da IA</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={pipelineId} onValueChange={setPipelineId}>
                  <SelectTrigger className="h-8 w-36 text-xs bg-white/15 border-white/30 text-white"><SelectValue placeholder="Pipeline..." /></SelectTrigger>
                  <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={responsavelId} onValueChange={setResponsavelId}>
                  <SelectTrigger className="h-8 w-36 text-xs bg-white/15 border-white/30 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Todos</SelectItem>
                    {responsaveis.map(r => <SelectItem key={r.user_id} value={r.user_id} className="text-xs">{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => onClose(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-colors flex-shrink-0 ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : !data ? (
              <div className="text-center py-20 text-slate-400 text-sm">Selecione uma pipeline para ver os insights.</div>
            ) : (
              <>
                {/* KPI Cards with gauge */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Score Gauge */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col items-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Saúde da Pipeline</p>
                    <ScoreGauge score={data.resumo.avg_score} />
                  </div>
                  {/* Negócios Ativos */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Negócios Ativos</p>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc}15` }}>
                        <TrendingUp className="w-3.5 h-3.5" style={{ color: tc }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{data.resumo.total_deals}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400">{data.resumo.deals_com_ia} com IA</span>
                      <TrendBadge current={data.comparacao?.deals_ganhos_atual} previous={data.comparacao?.deals_ganhos_anterior} suffix=" ganhos" />
                    </div>
                  </div>
                  {/* MRR Médio */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">MRR Médio</p>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc}15` }}>
                        <DollarSign className="w-3.5 h-3.5" style={{ color: tc }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: tc }}>{fmt(data.resumo.avg_mrr)}</p>
                    <TrendBadge current={data.comparacao?.mrr_ganho_atual} previous={data.comparacao?.mrr_ganho_anterior} suffix=" vs mês ant." />
                  </div>
                  {/* Tempo Médio */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Tempo na Etapa</p>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc}15` }}>
                        <Clock className="w-3.5 h-3.5" style={{ color: tc }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{Math.round(data.resumo.avg_days_in_stage)} <span className="text-sm text-slate-400">dias</span></p>
                  </div>
                </div>

                {/* Forecast */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <SectionTitle icon={BarChart3} title="Forecast de Receita (MRR)" color={tc} />
                  <div className="space-y-3">
                    {[
                      { label: "Conservador", desc: "Score > 85", value: data.forecast.conservador, color: "#64748b", bg: "bg-slate-50" },
                      { label: "Realista", desc: "Score > 70", value: data.forecast.realista, color: tc, bg: "" },
                      { label: "Otimista", desc: "Score > 50", value: data.forecast.otimista, color: "#10b981", bg: "bg-emerald-50/50" },
                    ].map(f => {
                      const maxVal = Math.max(data.forecast.otimista, 1);
                      const pct = maxVal > 0 ? (f.value / maxVal) * 100 : 0;
                      return (
                        <div key={f.label} className={`flex items-center gap-4 p-3 rounded-lg ${f.bg}`} style={f.color === tc ? { backgroundColor: `${tc}08` } : undefined}>
                          <div className="w-24 flex-shrink-0">
                            <p className="text-xs font-semibold" style={{ color: f.color }}>{f.label}</p>
                            <p className="text-[10px] text-slate-400">{f.desc}</p>
                          </div>
                          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: f.color }} />
                          </div>
                          <p className="text-sm font-bold w-24 text-right" style={{ color: f.color }}>{fmt(f.value)}</p>
                        </div>
                      );
                    })}
                  </div>
                  {Object.keys(data.forecast.historico).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Realizado (últimos 3 meses)</p>
                      <div className="flex gap-4">
                        {Object.entries(data.forecast.historico).map(([k, v]) => (
                          <div key={k} className="text-center flex-1 p-2 rounded-lg bg-slate-50">
                            <p className="text-[10px] text-slate-400">{k}</p>
                            <p className="text-sm font-bold text-slate-700">{fmt(v)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Top Oportunidades + Em Risco */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <SectionTitle icon={Flame} title="Top Oportunidades" color="#10b981" />
                    <div className="space-y-1.5">
                      {data.top_oportunidades.map(d => (
                        <button key={d.id} onClick={() => handleDealClick(d.id)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group">
                          <ScoreBar score={d.score} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{d.titulo}</p>
                            <p className="text-[10px] text-slate-400">{d.stage} · {d.responsavel} · {d.days_in_stage}d</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-bold" style={{ color: tc }}>{fmt(d.mrr)}</p>
                          </div>
                          <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                        </button>
                      ))}
                      {data.top_oportunidades.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Nenhum negócio encontrado.</p>}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <SectionTitle icon={AlertTriangle} title="Negócios em Risco" color="#ef4444" />
                    <div className="space-y-1.5">
                      {data.em_risco.map(d => (
                        <button key={d.id} onClick={() => handleDealClick(d.id)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group">
                          <ScoreBar score={d.score} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{d.titulo}</p>
                            <p className="text-[10px] text-slate-400">{d.stage} · {d.days_in_stage} dias parado</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-bold text-rose-500">{fmt(d.mrr)}</p>
                          </div>
                          <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                        </button>
                      ))}
                      {data.em_risco.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Nenhum negócio em risco.</p>}
                    </div>
                  </div>
                </div>

                {/* Ações Pendentes — clickable */}
                {data.acoes_pendentes.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <SectionTitle icon={CheckSquare} title="Ações Pendentes (sugeridas pela IA)" color={tc} />
                    <div className="space-y-1 max-h-56 overflow-y-auto">
                      {data.acoes_pendentes.map((a, i) => (
                        <button key={i} onClick={() => handleDealClick(a.deal_id)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group">
                          <ScoreBar score={a.score} />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-700">{a.titulo}</span>
                            <p className="text-[10px] text-slate-400 mt-0.5">{acaoLabels[a.acao] || a.acao}: {a.descricao}</p>
                          </div>
                          {a.data && <span className="text-[10px] text-slate-400 flex-shrink-0">{a.data}</span>}
                          <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Distribuição Equipe */}
                {data.distribuicao_equipe.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <SectionTitle icon={Users} title="Distribuição da Equipe" color={tc} />
                    <div className="space-y-2.5">
                      {data.distribuicao_equipe.sort((a, b) => b.total_mrr - a.total_mrr).map(r => {
                        const total = r.quente + r.morno + r.frio || 1;
                        return (
                          <div key={r.responsavel} className="flex items-center gap-3">
                            <span className="text-sm font-medium text-slate-700 w-28 truncate">{r.responsavel}</span>
                            <div className="flex-1 flex h-5 rounded-full overflow-hidden bg-slate-100">
                              {r.quente > 0 && <div className="bg-emerald-500 h-full flex items-center justify-center" style={{ width: `${(r.quente / total) * 100}%` }}><span className="text-[9px] text-white font-bold">{r.quente}</span></div>}
                              {r.morno > 0 && <div className="bg-amber-400 h-full flex items-center justify-center" style={{ width: `${(r.morno / total) * 100}%` }}><span className="text-[9px] text-white font-bold">{r.morno}</span></div>}
                              {r.frio > 0 && <div className="bg-rose-400 h-full flex items-center justify-center" style={{ width: `${(r.frio / total) * 100}%` }}><span className="text-[9px] text-white font-bold">{r.frio}</span></div>}
                            </div>
                            <span className="text-xs font-bold text-slate-600 w-20 text-right">{fmt(r.total_mrr)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-4 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Quente (70+)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Morno (40-69)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" />Frio (&lt;40)</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
